import { useState, useEffect, useCallback } from "react";
import { useOrganizationStore } from "@/store/useOrganizationStore";
import {
  updateOrganization,
  OnboardingSurvey
} from "@/services/OrganizationService";
import type { OnboardingSurveyData } from "@/components/onboarding/OnboardingSurveyPopup";

interface UseOnboardingSurveyReturn {
  showSurvey: boolean;
  surveyData: OnboardingSurveyData;
  saveSurveyData: (data: Partial<OnboardingSurveyData>) => Promise<void>;
  closeSurvey: () => void | Promise<void>;
  isSurveyCompleted: boolean;
}

/**
 * Hook to manage the onboarding survey state and persistence.
 *
 * The survey is shown only once per organization when:
 * - Organization data is loaded
 * - onboardingSurvey field is undefined/null (never shown before)
 *
 * Data is saved incrementally at each step to persist partial completions.
 */
export const useOnboardingSurvey = (): UseOnboardingSurveyReturn => {
  const [showSurvey, setShowSurvey] = useState(false);
  const [surveyData, setSurveyData] = useState<OnboardingSurveyData>({});
  const [isSurveyCompleted, setIsSurveyCompleted] = useState(false);
  const [hasCheckedSurvey, setHasCheckedSurvey] = useState(false);

  const organization = useOrganizationStore((state) => state.organization);
  const setOrganization = useOrganizationStore(
    (state) => state.setOrganization
  );

  // Check if survey should be shown when organization data loads
  useEffect(() => {
    if (!organization?.id || hasCheckedSurvey) return;

    // Get onboardingSurvey from organization (cast to any since type may not be updated yet)
    const existingSurvey = (organization as any).onboardingSurvey as
      | OnboardingSurvey
      | undefined;

    if (existingSurvey) {
      // Survey was previously closed or completed (stored as {} or with data)
      setSurveyData({
        howDidYouHear: existingSurvey.howDidYouHear,
        lookingToDo: existingSurvey.lookingToDo,
        mobileNumber: existingSurvey.mobileNumber
      });

      // Check if all required fields are filled (survey completed)
      // We consider completed if at least step 1 and 2 have data
      const isCompleted = !!(
        existingSurvey.howDidYouHear && existingSurvey.lookingToDo
      );
      setIsSurveyCompleted(isCompleted);
      setShowSurvey(false);
    } else {
      // Survey never shown - show it
      setShowSurvey(true);
      setIsSurveyCompleted(false);
    }

    setHasCheckedSurvey(true);
  }, [
    organization?.id,
    (organization as any)?.onboardingSurvey,
    hasCheckedSurvey
  ]);

  /**
   * Save partial or complete survey data to the backend.
   * Called at each step to persist progress even if user doesn't complete the survey.
   */
  const saveSurveyData = useCallback(
    async (data: Partial<OnboardingSurveyData>) => {
      try {
        // Merge with existing data
        const mergedData: OnboardingSurvey = {
          ...surveyData,
          ...data
        };

        // Update local state immediately
        setSurveyData(mergedData);

        // Persist to backend (cast to any since shared types may not have onboardingSurvey yet)
        const response = await updateOrganization({
          onboardingSurvey: mergedData
        } as any);

        // Update organization in store with new survey data
        if (response) {
          setOrganization(response);
        }

        // Check if survey is now complete
        if (mergedData.howDidYouHear && mergedData.lookingToDo) {
          setIsSurveyCompleted(true);
        }
      } catch (error) {
        console.error("Failed to save onboarding survey data:", error);
        // Don't throw - we want the UI to continue even if save fails
      }
    },
    [surveyData, setOrganization]
  );

  /**
   * Close the survey popup.
   * Persists current state (or empty {}) to backend so the survey is not shown again
   * on refresh/login, then hides the popup.
   */
  const closeSurvey = useCallback(async () => {
    // only persist if nothing has ever been saved yet
    const hasAnyData =
      !!surveyData.howDidYouHear ||
      !!surveyData.lookingToDo ||
      !!surveyData.mobileNumber;

    if (!hasAnyData) {
      try {
        await updateOrganization({ onboardingSurvey: {} } as any);
      } catch (e) {
        console.error("Failed to persist survey close:", e);
      }
    }

    setShowSurvey(false);
  }, [surveyData]);

  return {
    showSurvey,
    surveyData,
    saveSurveyData,
    closeSurvey,
    isSurveyCompleted
  };
};

export default useOnboardingSurvey;
